import { useState } from 'react';
import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { Cube20Regular, DocumentPdf20Regular, DrawShape20Regular } from '@fluentui/react-icons';
import type { PlanoGaleriaItem } from '../../api/planos.js';

/**
 * Vista previa de un plano (16:10). Tres estados, y ninguno bloquea abrir el plano:
 *  - hay imagen → la imagen;
 *  - se está preparando → brillo que avanza + "Preparando la vista previa…";
 *  - no hay / falló / la imagen no cargó → el ícono grande del tipo, sin mensaje de error.
 */
const useStyles = makeStyles({
  marco: {
    position: 'relative',
    width: '100%',
    aspectRatio: '16 / 10',
    overflow: 'hidden',
    background: 'var(--bg-raised)',
    display: 'grid',
    placeItems: 'center',
    color: 'var(--text-tertiary)',
  },
  img: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  brillo: {
    position: 'absolute',
    inset: 0,
    backgroundImage:
      'linear-gradient(100deg, transparent 20%, var(--bg-hover) 50%, transparent 80%)',
    backgroundSize: '200% 100%',
    animationName: {
      from: { backgroundPosition: '150% 0' },
      to: { backgroundPosition: '-50% 0' },
    },
    animationDuration: '1.6s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'linear',
  },
  nota: { position: 'relative', fontSize: '12px', color: 'var(--text-tertiary)' },
  icono: { width: '44px', height: '44px', opacity: 0.7 },
  iconoChico: { width: '18px', height: '18px', opacity: 0.8 },
});

export function IconoTipoPlano({ tipo, chico }: { tipo: PlanoGaleriaItem['tipo']; chico?: boolean }) {
  const styles = useStyles();
  const cls = chico ? styles.iconoChico : styles.icono;
  if (tipo === 'ifc') return <Cube20Regular className={cls} />;
  if (tipo === 'dwg') return <DrawShape20Regular className={cls} />;
  return <DocumentPdf20Regular className={cls} />;
}

export function MiniaturaPlano({
  plano,
  chica,
  className,
}: {
  plano: Pick<PlanoGaleriaItem, 'tipo' | 'miniatura' | 'miniaturaUrl'>;
  /** Versión de la tira de planos: sin texto, ícono chico. */
  chica?: boolean;
  className?: string;
}) {
  const styles = useStyles();
  const [imgFallo, setImgFallo] = useState(false);

  let contenido: JSX.Element;
  if (plano.miniaturaUrl && !imgFallo) {
    contenido = (
      <img
        className={styles.img}
        src={plano.miniaturaUrl}
        alt=""
        loading="lazy"
        onError={() => setImgFallo(true)}
      />
    );
  } else if (plano.miniatura === 'pending') {
    contenido = (
      <>
        <span className={styles.brillo} aria-hidden />
        {!chica && <span className={styles.nota}>Preparando la vista previa…</span>}
      </>
    );
  } else {
    contenido = <IconoTipoPlano tipo={plano.tipo} chico={chica} />;
  }
  return <div className={mergeClasses(styles.marco, className)}>{contenido}</div>;
}
